import { useState } from 'react';
import { MessageSquare, Pencil, Trash2, Check, X } from 'lucide-react';
import { creditLimitsApi } from '../../services/creditLimitsApi';
import type { CreditLimitComment } from '../../types/creditLimits';

interface CreditLimitCommentsProps {
    customerCode: string;
    comments: CreditLimitComment[];
    canEdit: boolean;
    onCommentChanged: () => void;
}

const COMMENT_TYPES = [
    { value: 'note', label: 'Note', color: 'bg-gray-100 text-gray-700' },
    { value: 'action', label: 'Action', color: 'bg-blue-100 text-blue-700' },
    { value: 'decision', label: 'Decision', color: 'bg-green-100 text-green-700' },
];

function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('de-CH', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}

export function CreditLimitComments({ customerCode, comments, canEdit, onCommentChanged }: CreditLimitCommentsProps) {
    const [newText, setNewText] = useState('');
    const [newType, setNewType] = useState('note');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editText, setEditText] = useState('');
    const [loading, setLoading] = useState(false);

    const handleAdd = async () => {
        if (!newText.trim()) return;
        setLoading(true);
        try {
            await creditLimitsApi.createComment(customerCode, newText.trim(), newType);
            setNewText('');
            setNewType('note');
            onCommentChanged();
        } catch (err) {
            console.error('Failed to create comment:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (commentId: number) => {
        if (!editText.trim()) return;
        setLoading(true);
        try {
            await creditLimitsApi.updateComment(customerCode, commentId, editText.trim());
            setEditingId(null);
            onCommentChanged();
        } catch (err) {
            console.error('Failed to update comment:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (commentId: number) => {
        setLoading(true);
        try {
            await creditLimitsApi.deleteComment(customerCode, commentId);
            onCommentChanged();
        } catch (err) {
            console.error('Failed to delete comment:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <h4 className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                <MessageSquare className="h-4 w-4" /> Comments ({comments.length})
            </h4>

            {/* Comment list */}
            <div className="space-y-2 mb-3">
                {comments.map(c => (
                    <div key={c.Id} className="rounded border border-gray-200 p-2 text-sm">
                        <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                                <span className={`px-1.5 py-0.5 text-xs rounded ${
                                    COMMENT_TYPES.find(t => t.value === c.CommentType)?.color || 'bg-gray-100'
                                }`}>
                                    {c.CommentType}
                                </span>
                                <span className="text-xs text-gray-500">{c.CreatedBy}</span>
                                <span className="text-xs text-gray-400">{formatDate(c.CreatedAt)}</span>
                                {c.UpdatedAt && <span className="text-xs text-gray-400 italic">(edited)</span>}
                            </div>
                            {canEdit && editingId !== c.Id && (
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => { setEditingId(c.Id); setEditText(c.CommentText); }}
                                        className="p-1 text-gray-400 hover:text-gray-600"
                                    >
                                        <Pencil className="h-3 w-3" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(c.Id)}
                                        className="p-1 text-gray-400 hover:text-red-600"
                                        disabled={loading}
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </button>
                                </div>
                            )}
                        </div>
                        {editingId === c.Id ? (
                            <div className="flex gap-2 mt-1">
                                <input
                                    type="text"
                                    value={editText}
                                    onChange={e => setEditText(e.target.value)}
                                    className="flex-1 text-sm border border-gray-300 rounded px-2 py-1"
                                    onKeyDown={e => { if (e.key === 'Enter') handleUpdate(c.Id); if (e.key === 'Escape') setEditingId(null); }}
                                />
                                <button onClick={() => handleUpdate(c.Id)} disabled={loading} className="p-1 text-green-600 hover:text-green-700">
                                    <Check className="h-4 w-4" />
                                </button>
                                <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:text-gray-600">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        ) : (
                            <p className="text-gray-700">{c.CommentText}</p>
                        )}
                    </div>
                ))}
                {comments.length === 0 && (
                    <p className="text-xs text-gray-400">No comments yet.</p>
                )}
            </div>

            {/* Add new comment */}
            {canEdit && (
                <div className="flex gap-2">
                    <div className="flex gap-1">
                        {COMMENT_TYPES.map(t => (
                            <button
                                key={t.value}
                                onClick={() => setNewType(t.value)}
                                className={`px-2 py-1 text-xs rounded ${
                                    newType === t.value ? t.color + ' ring-1 ring-gray-400' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <input
                        type="text"
                        value={newText}
                        onChange={e => setNewText(e.target.value)}
                        placeholder="Add comment..."
                        className="flex-1 text-sm border border-gray-300 rounded px-2 py-1"
                        onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
                    />
                    <button
                        onClick={handleAdd}
                        disabled={loading || !newText.trim()}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                        Add
                    </button>
                </div>
            )}
        </div>
    );
}
